/* The one mapping from a query's signals to the state `AsyncSection` renders.
   Its own file so AsyncSection.tsx stays one component under the 200-line
   cap (packages/ui/CLAUDE.md invariant 6); data-blind like the component. */

export type AsyncStatus = "loading" | "error" | "empty" | "ready";

export interface AsyncStatusInput {
  /** First load in flight, with nothing to show yet. */
  readonly isLoading: boolean;
  /** The query has an error — which does NOT mean the region is empty. */
  readonly isError: boolean;
  /** Settled with genuinely zero rows. */
  readonly isEmpty: boolean;
  /**
   * Whether anything is already on screen.
   *
   * This is the load-bearing input, and the reason the four states are not a
   * simple precedence chain. `useCursorPagination` reports the error of the
   * WHOLE infinite query, and TanStack keeps `data` while a background refetch
   * fails — so `isError` goes true with rows visible, on a failed *Load more*
   * or a failed refresh. A plain error-beats-everything ordering would throw
   * away the page the reader was looking at, which is a worse failure than the
   * one it reports. With data, the region stays `ready` and the caller shows
   * the failure beside the rows instead.
   */
  readonly hasData: boolean;
}

/**
 * The one mapping from a query's signals to what a region should render.
 *
 * Order: a failure with nothing on screen wins, then loading, then empty.
 * `isEmpty` needs no guard against error — every caller derives it from a
 * SUCCESSFUL settle (`isSuccess && length === 0`), so it cannot be true at the
 * same time as a first-load failure.
 */
export function asyncStatus({
  isLoading,
  isError,
  isEmpty,
  hasData,
}: AsyncStatusInput): AsyncStatus {
  if (isError && !hasData) return "error";
  if (isLoading && !hasData) return "loading";
  if (isEmpty && !hasData) return "empty";
  return "ready";
}
